import loadPreactModule from "../../../utils/loadPreactModule";
import renderPreactWithProps from "../../../utils/renderPreactWithProps";
import callIfArgumentsPass from "../../../utils/callIfArgumentsPass";
import curry from "../../../lib/core/functional/curry";
import noop from "../../../lib/core/functional/noop";
import { getFolders, saveToFolder } from "../../../transport/folders";
import { loadInformerAsModule } from "../../informer/loader";
import raiseNotification from "../../../utils/raiseNotification";
import { didUserInfoChanged, getUserCommunity, canManageLabels } from "../_common";

function decorateFolder(folder) {
    return { ...folder, icon: window.RM_FOLDER_ICONS[folder.slug] };
}

function decorateFolders(folders) {
    if (window.RM_FOLDER_ICONS && Object.keys(window.RM_FOLDER_ICONS).length > 0) {
        return folders.map(decorateFolder);
    } else {
        return folders;
    }
}

function markAsCustom(folder) {
    return { ...folder, type: "custom" };
}

function markAsGlobal(folder) {
    return { ...folder, type: "global" };
}

export const renderFoldersButton = curry(function renderFoldersButton(
    renderModule,
    postId,
    getFolders,
    saveToFolder,
    err,
    userInfo
) {
    if (err) {
        renderModule({
            success: null,
            folders: [],
            teamFolders: [],
            isLoggedIn: false,
            getFolders: noop,
            addToFolders: noop,
            disabled: false,
        });
        return;
    }

    const community = getUserCommunity(userInfo);
    const canManageOwnLabels = canManageLabels(userInfo);

    if (!community && !canManageOwnLabels) {
        renderModule({
            success: null,
            folders: [],
            teamFolders: [],
            isLoggedIn: true,
            getFolders: noop,
            addToFolders: noop,
            disabled: true,
        });
        return;
    }

    let folders = [];
    let teamFolders = [];
    let success = null;

    function render() {
        renderModule({
            success,
            addToFolders,
            folders,
            teamFolders,
            getFolders: getAllFolders,
            isLoggedIn: true,
            disabled: false,
        });
    }

    function onSave() {
        success = true;
    }

    function onError() {
        success = false;
    }

    function addToFolders(folders, teamFolders) {
        const promises = [saveToFolder(null, postId, folders)];
        if (community && teamFolders.length > 0) {
            promises.push(saveToFolder(community, postId, teamFolders));
        }

        return Promise.all(promises).then(onSave).catch(onError).finally(render);
    }

    function onFolders([
        userGlobalFolders,
        userCustomFolders,
        communityGlobalFolders,
        communityCustomFolders,
    ]) {
        if (userGlobalFolders && userCustomFolders) {
            folders = decorateFolders([
                ...userGlobalFolders.labels.map(markAsGlobal),
                ...userCustomFolders.labels.map(markAsCustom),
            ]);
        } else {
            folders = [];
        }

        if (communityGlobalFolders && communityCustomFolders) {
            teamFolders = decorateFolders([
                ...communityGlobalFolders.labels.map(markAsGlobal),
                ...communityCustomFolders.labels.map(markAsCustom),
            ]);
        } else {
            teamFolders = [];
        }
        render();
    }

    function getAllFolders() {
        success = null;
        render();
        const promises = [];

        if (canManageOwnLabels) {
            promises.push(getFolders(), getFolders(true));
        } else {
            promises.push(Promise.resolve(null), Promise.resolve(null));
        }

        if (community) {
            promises.push(getFolders(false, community), getFolders(true, community));
        }

        Promise.all(promises).then(onFolders).catch(onError).finally(render);
    }

    render();
});

const onModule = curry(function onModule(context, element, [renderer, Module]) {
    const parentPost = element.closest("article[elid]");
    const postId = parentPost.getAttribute("elid");

    if (!postId) {
        return;
    }

    context.listenForUserInfo(
        callIfArgumentsPass(
            didUserInfoChanged,
            renderFoldersButton(
                renderPreactWithProps(renderer, Module, element),
                postId,
                getFolders,
                saveToFolder
            )
        )
    );
});

const internalLoader = () => import("./module").then((module) => module.default);

export default function renderer(context, element) {
    function onInformer(Informer) {
        loadPreactModule(
            "FoldersButton",
            context,
            { Informer, raiseNotification },
            internalLoader
        ).then(onModule(context, element));
    }

    loadInformerAsModule(context).then(onInformer);
}
